import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import { useShipping } from "../../hooks/useShipping";
import { useAuth } from "../../context/AuthContext";
import { Button, Callout } from "../ui";

// Import components
import { UnitSystem } from "./UnitSystem";
import {
  Parcel,
  createEmptyParcel,
  updateParcelWeights,
} from "./utils/calculationUtils";
import { mapDocumentsToParcels } from "./utils/documentCommodityUtils";

// Import subcomponents
import {
  ShippingForm,
  ShippingRates,
  ShippingLoadingAnimation,
  AddressSelection,
  ShippingType,
  ShippingRate,
  Contact,
  Document,
} from "./components";
import AuthDialog from "./components/AuthDialog";

// Create empty document (max weight 0.5kg)
const createEmptyDocument = (weight?: number): Document => ({
  id: Math.random().toString(36).substring(2, 9),
  quantity: 1,
  weight: weight && weight <= 0.5 ? weight : 0.5,
});

enum ShippingStep {
  FORM = 0,
  LOADING = 1,
  RATES = 2,
  ADDRESS = 3,
}

type ShippingCalculatorProps = {
  currentState?: {
    originCountry: string;
    destinationCountry: string;
    Weight: number;
    dimensions?: { length: number; width: number; height: number };
    unitSystem?: "metric" | "imperial";
    shippingType?: "package" | "document";
    envelopeSize?: "small" | "large";
    shipmentType?: "international" | "domestic";
    parcels?: Parcel[];
    documents?: Document[];
    autoGetRates?: boolean;
    cachedRates?: ShippingRate[];
    selectedRateId?: string;
    selectedSourceAddress?: Contact;
    selectedDestinationAddress?: Contact;
    selectedRateProviderCode?: string;
  };
  onStepChange?: (step: ShippingStep, replaceHistory?: boolean) => void;
  currentStep?: ShippingStep;
  onCacheData?: (data: any) => void;
};

const ShippingCalculator: React.FC<ShippingCalculatorProps> = ({
  currentState,
  onStepChange,
  currentStep: externalCurrentStep,
  onCacheData,
}) => {
  const navigate = useNavigate();
  const { isRTL } = useLanguage();
  const { isAuthenticated } = useAuth();
  const errorRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Detect mobile via window width (no MUI useMediaQuery)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 900);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const {
    getShippingRates,
    shippingRates: apiRates,
    isLoadingRates,
    ratesError,
    apiError,
    apiWarning,
  } = useShipping();

  const [internalCurrentStep, setInternalCurrentStep] = useState<ShippingStep>(
    externalCurrentStep ?? ShippingStep.FORM
  );
  const currentStep = externalCurrentStep ?? internalCurrentStep;

  const [isGetShippingRatesClicked, setIsGetShippingRatesClicked] = useState(false);
  const [userOriginCountry, setUserOriginCountry] = useState(currentState?.originCountry || "");
  const [userDestinationCountry, setUserDestinationCountry] = useState(currentState?.destinationCountry || "");
  const [originPostalCode, setOriginPostalCode] = useState("");
  const [destinationPostalCode, setDestinationPostalCode] = useState("");
  const [unitSystem, setUnitSystem] = useState<UnitSystem>(currentState?.unitSystem || "metric");
  const [parcels, setParcels] = useState<Parcel[]>(
    currentState?.parcels && currentState.parcels.length > 0
      ? currentState.parcels
      : [createEmptyParcel(currentState?.Weight, currentState?.dimensions)]
  );
  const [documents, setDocuments] = useState<Document[]>(
    currentState?.documents && currentState.documents.length > 0
      ? currentState.documents
      : [createEmptyDocument(currentState?.Weight)]
  );
  const [error, setError] = useState("");

  const [shippingType, setShippingType] = useState<ShippingType>(currentState?.shippingType || "package");
  const [shipmentType, setShipmentType] = useState<"international" | "domestic">(
    currentState?.shipmentType || "international"
  );
  const [shippingRates, setShippingRates] = useState<ShippingRate[]>(currentState?.cachedRates || []);
  const [selectedRateId, setSelectedRateId] = useState<string | null>(currentState?.selectedRateId || null);
  const [selectedSourceAddress, setSelectedSourceAddress] = useState<Contact | null>(
    currentState?.selectedSourceAddress || null
  );
  const [selectedDestinationAddress, setSelectedDestinationAddress] = useState<Contact | null>(
    currentState?.selectedDestinationAddress || null
  );

  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [isMobileFormVisible, setIsMobileFormVisible] = useState(true);

  /* ── step helper ─────────────────────────────────────────────────────────── */
  const updateCurrentStep = (step: ShippingStep, replaceHistory = false) => {
    if (onStepChange) {
      onStepChange(step, replaceHistory);
    } else {
      setInternalCurrentStep(step);
    }
    if (isMobile && step !== ShippingStep.FORM) {
      setIsMobileFormVisible(false);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    } else if (step === ShippingStep.FORM) {
      setIsMobileFormVisible(true);
    }
  };

  const toggleMobileForm = () => setIsMobileFormVisible((v) => !v);

  /* ── effects ─────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (
      currentState?.autoGetRates &&
      userOriginCountry &&
      userDestinationCountry &&
      !isGetShippingRatesClicked &&
      !currentState?.cachedRates
    ) {
      const timer = setTimeout(() => {
        handleGetRates({
          originCountry: userOriginCountry,
          destinationCountry: userDestinationCountry,
          originPostalCode,
          destinationPostalCode,
        });
      }, 100);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentState?.autoGetRates, userOriginCountry, userDestinationCountry, parcels, documents]);

  useEffect(() => {
    if (currentState?.cachedRates && currentState.cachedRates.length > 0) {
      setShippingRates(currentState.cachedRates);
      setIsGetShippingRatesClicked(true);
      if (externalCurrentStep === ShippingStep.ADDRESS) {
        updateCurrentStep(ShippingStep.ADDRESS, true);
      } else if (externalCurrentStep === ShippingStep.RATES) {
        updateCurrentStep(ShippingStep.RATES, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalCurrentStep]);

  useEffect(() => {
    if (isLoadingRates) updateCurrentStep(ShippingStep.LOADING);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingRates]);

  useEffect(() => {
    if (!isGetShippingRatesClicked) return;
    if (apiRates && apiRates.length > 0) {
      setShippingRates(apiRates);
      updateCurrentStep(ShippingStep.RATES);
    } else if (apiRates && apiRates.length === 0 && !isLoadingRates) {
      setError("No shipping rates available for the selected criteria.");
      updateCurrentStep(ShippingStep.FORM);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(apiRates), isLoadingRates, isGetShippingRatesClicked]);

  useEffect(() => {
    if (ratesError) {
      const responseData = (ratesError as any)?.response?.data;
      const msg =
        responseData?.message ||
        responseData?.error ||
        responseData?.error_message ||
        "Failed to fetch shipping rates. Please try again.";
      setError(msg);
      updateCurrentStep(ShippingStep.FORM);
      scrollToError();
    } else if (apiError && apiError.success === false) {
      const msg = apiError.error_message || (apiError as any).error || "An error occurred while fetching shipping rates.";
      setError(msg);
      updateCurrentStep(ShippingStep.FORM);
      scrollToError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratesError, apiError]);

  /* ── helpers ─────────────────────────────────────────────────────────────── */
  const scrollToError = () => {
    setTimeout(() => errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  };

  const swapLocations = () => {
    const tmpCountry = userOriginCountry;
    const tmpPostal = originPostalCode;
    setUserOriginCountry(userDestinationCountry);
    setOriginPostalCode(destinationPostalCode);
    setUserDestinationCountry(tmpCountry);
    setDestinationPostalCode(tmpPostal);
  };

  const handleUnitSystemChange = (next: UnitSystem) => {
    if (next !== unitSystem) setUnitSystem(next);
  };

  const handleShippingTypeChange = (_: React.MouseEvent<HTMLElement>, next: ShippingType | null) => {
    if (next !== null) setShippingType(next);
  };

  const handleShipmentTypeChange = (next: "international" | "domestic") => setShipmentType(next);

  /* ── document handlers ───────────────────────────────────────────────────── */
  const addDocument = () => setDocuments((d) => [...d, createEmptyDocument()]);
  const removeDocument = (id: string) => {
    if (documents.length > 1) setDocuments((d) => d.filter((doc) => doc.id !== id));
  };
  const updateDocument = (id: string, field: string, value: any) => {
    setDocuments((d) => d.map((doc) => (doc.id === id ? { ...doc, [field]: value } : doc)));
  };

  /* ── parcel handlers ─────────────────────────────────────────────────────── */
  const addParcel = () => setParcels((p) => [...p, createEmptyParcel()]);
  const removeParcel = (id: string) => {
    if (parcels.length > 1) setParcels((p) => p.filter((parcel) => parcel.id !== id));
  };
  const updateParcel = (id: string, field: string, value: number) => {
    setParcels((p) =>
      p.map((parcel) => {
        if (parcel.id !== id) return parcel;
        const updated = { ...parcel, [field]: value };
        return updateParcelWeights(updated, unitSystem);
      })
    );
  };

  const handleOriginPostalCodeChange = (v: string) => setOriginPostalCode(v);
  const handleDestinationPostalCodeChange = (v: string) => setDestinationPostalCode(v);

  /* ── weight calculation ──────────────────────────────────────────────────── */
  const calculateTotalWeight = (): number => {
    if (shippingType === "package") {
      return parcels.reduce((total, p) => total + Math.max(p.physicalWeight || 0, p.dimensionalWeight || 0), 0);
    }
    return documents.reduce((total, doc) => total + doc.weight * doc.quantity, 0);
  };

  /* ── get rates ───────────────────────────────────────────────────────────── */
  const handleGetRates = async (data: {
    originCountry: string;
    destinationCountry: string;
    originPostalCode?: string;
    destinationPostalCode?: string;
  }) => {
    setIsGetShippingRatesClicked(true);
    setUserOriginCountry(data.originCountry);
    setUserDestinationCountry(data.destinationCountry);
    if (data.originPostalCode) setOriginPostalCode(data.originPostalCode);
    if (data.destinationPostalCode) setDestinationPostalCode(data.destinationPostalCode);
    setError("");

    if (shippingType === "package") {
      const ok = parcels.length > 0 && parcels.every((p) => p.weight > 0 && p.length > 0 && p.width > 0 && p.height > 0);
      if (!ok) {
        setError("Please enter valid package details.");
        return;
      }
    } else {
      const ok = documents.length > 0 && documents.every((d) => d.quantity > 0 && d.weight > 0 && d.weight <= 0.5);
      if (!ok) {
        setError("Please enter valid document details (max weight 0.5kg or 1.1lb).");
        return;
      }
    }

    updateCurrentStep(ShippingStep.LOADING);

    try {
      const isInternational = data.originCountry !== data.destinationCountry;
      const totalWeight = calculateTotalWeight();
      const requestData = {
        origin: data.originCountry,
        destination: data.destinationCountry,
        originPostalCode: data.originPostalCode || "",
        destinationPostalCode: data.destinationPostalCode || "",
        shippingType,
        shipmentType: isInternational ? "international" : "domestic",
        // Documents are tracked separately; the rate API only accepts a
        // `parcels` array, so convert documents to parcels for document searches.
        parcels:
          shippingType === "document"
            ? mapDocumentsToParcels(documents)
            : parcels.map((p) => ({
                quantity: parseFloat(p.quantity.toString()),
                weight: parseFloat(p.weight.toString()),
                length: parseFloat(p.length.toString()),
                width: parseFloat(p.width.toString()),
                height: parseFloat(p.height.toString()),
                physicalWeight: parseFloat(p.physicalWeight.toString()),
                dimensionalWeight: parseFloat(p.dimensionalWeight.toString()),
              })),
        totalWeight: parseFloat(totalWeight.toString()),
        unitSystem,
        isInternational,
        compareAllProviders: true,
      };
      setSelectedRateId(null);
      await getShippingRates(requestData);
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Failed to fetch shipping rates. Please try again.";
      setError(msg);
      updateCurrentStep(ShippingStep.FORM);
    }
  };

  /* ── rate selection ──────────────────────────────────────────────────────── */
  const handleRateSelect = (rateId: string) => {
    const selectedRate = shippingRates.find((r) => r.id === rateId);
    if (!selectedRate) {
      console.warn("Selected rate not found in rates array");
      return;
    }
    setSelectedRateId(rateId);
    if (!isAuthenticated) {
      setShowAuthDialog(true);
      return;
    }
    if (onCacheData) {
      onCacheData({
        origin: userOriginCountry,
        destination: userDestinationCountry,
        originPostalCode,
        destinationPostalCode,
        shippingType,
        shipmentType,
        parcels,
        documents,
        unitSystem,
        rates: shippingRates,
        selectedRateId: rateId,
        selectedSourceAddress,
        selectedDestinationAddress,
        selectedRateProviderCode: selectedRate.provider,
      });
    }
    updateCurrentStep(ShippingStep.ADDRESS);
  };

  const handleContinueToDelivery = async () => {
    if (!selectedRateId) return;
    if (!isAuthenticated) {
      setShowAuthDialog(true);
      return;
    }
    updateCurrentStep(ShippingStep.ADDRESS);
  };

  const handleAuthSuccess = () => {
    setShowAuthDialog(false);
    if (selectedRateId) updateCurrentStep(ShippingStep.ADDRESS);
  };

  const handleAuthDialogClose = () => {
    setShowAuthDialog(false);
    setSelectedRateId(null);
  };

  const handleBackToRates = () => updateCurrentStep(ShippingStep.RATES);

  const handleSourceAddressSelect = (address: Contact) => {
    setSelectedSourceAddress(address);
    if (typeof address?.zipCode !== "undefined") setOriginPostalCode(address.zipCode || "");
  };

  const handleDestinationAddressSelect = (address: Contact) => {
    setSelectedDestinationAddress(address);
    if (typeof address?.zipCode !== "undefined") setDestinationPostalCode(address.zipCode || "");
  };

  const handleContinueToPayment = (sourceAddress: Contact, destinationAddress: Contact) => {
    if (!sourceAddress || !destinationAddress) {
      setError("Please select both source and destination addresses");
      return;
    }
    const updatedOrigin = sourceAddress?.zipCode || originPostalCode || "";
    const updatedDestination = destinationAddress?.zipCode || destinationPostalCode || "";
    setSelectedSourceAddress(sourceAddress);
    setSelectedDestinationAddress(destinationAddress);
    setOriginPostalCode(updatedOrigin);
    setDestinationPostalCode(updatedDestination);

    const selectedRate = shippingRates.find((r) => r.id === selectedRateId);
    const cachedData = {
      origin: userOriginCountry,
      destination: userDestinationCountry,
      originPostalCode: updatedOrigin,
      destinationPostalCode: updatedDestination,
      shippingType,
      shipmentType,
      parcels,
      documents,
      unitSystem,
      rates: shippingRates,
      selectedRateId,
      selectedSourceAddress: sourceAddress,
      selectedDestinationAddress: destinationAddress,
      selectedRateProviderCode: selectedRate ? selectedRate.provider : null,
    };

    navigate("/shipping-order", {
      state: {
        origin: userOriginCountry,
        destination: userDestinationCountry,
        originPostalCode: updatedOrigin,
        destinationPostalCode: updatedDestination,
        shippingType,
        totalWeight: calculateTotalWeight(),
        parcels,
        documents,
        selectedSourceAddress: sourceAddress,
        selectedDestinationAddress: destinationAddress,
        unitSystem,
        shipmentType,
        isInternational: userOriginCountry !== userDestinationCountry,
        selectedRateProvider: selectedRate ? selectedRate.provider_display_name : null,
        selectedRateProviderCode: selectedRate ? selectedRate.provider : null,
        cachedShippingData: cachedData,
      },
    });
  };

  /* ── shared form props ───────────────────────────────────────────────────── */
  const shippingFormProps = {
    unitSystem,
    userOriginCountry,
    userDestinationCountry,
    shippingType,
    shipmentType,
    parcels,
    documents,
    isRTL,
    isLoading: isLoadingRates,
    originPostalCode,
    destinationPostalCode,
    onUnitSystemChange: handleUnitSystemChange,
    onShippingTypeChange: handleShippingTypeChange,
    onShipmentTypeChange: handleShipmentTypeChange,
    onAddParcel: addParcel,
    onRemoveParcel: removeParcel,
    onUpdateParcel: updateParcel,
    onAddDocument: addDocument,
    onRemoveDocument: removeDocument,
    onUpdateDocument: updateDocument,
    onSwapLocations: swapLocations,
    onOriginPostalCodeChange: handleOriginPostalCodeChange,
    onDestinationPostalCodeChange: handleDestinationPostalCodeChange,
    onSubmit: handleGetRates,
  };

  /* ── render ──────────────────────────────────────────────────────────────── */
  return (
    <div
      style={{
        borderRadius: isMobile ? 0 : 12,
        border: isMobile ? "none" : "1px solid var(--line)",
        overflow: "visible",
        width: "100%",
        background: "var(--bg-elev)",
      }}
    >
      <div style={{ padding: isMobile ? "4px" : "24px" }}>
        {/* Error banner */}
        {error && (
          <div ref={errorRef} style={{ marginBottom: isMobile ? 8 : 16 }}>
            <Callout tone="warn">{error}</Callout>
          </div>
        )}

        {/* Warning banner */}
        {apiWarning && (
          <div style={{ marginBottom: isMobile ? 8 : 16 }}>
            <Callout tone="warn">{apiWarning}</Callout>
          </div>
        )}

        {/* Layout container —
            FORM step: form full-width (100%)
            RATES / ADDRESS steps: side-by-side on desktop (form left, results right) */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              isMobile || currentStep === ShippingStep.FORM ? "1fr" : "minmax(0, 1fr) minmax(0, 1fr)",
            alignItems: "start",
            gap: isMobile ? 16 : 24,
            position: "relative",
          }}
        >
          {/* Mobile: collapsible form panel (when past FORM step) */}
          {isMobile && currentStep !== ShippingStep.FORM && (
            <div
              style={{
                display: isMobileFormVisible ? "block" : "none",
                marginBottom: isMobile ? 4 : 16,
                borderRadius: isMobile ? 6 : 10,
                overflow: "hidden",
                border: "1px solid var(--brand-blue)",
              }}
            >
              {/* Header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: isMobile ? "8px 10px" : "10px 14px",
                  background: "var(--brand-blue)",
                  color: "white",
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                  ✏️ Modify Search
                </span>
                <button
                  type="button"
                  onClick={toggleMobileForm}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "white", fontSize: 16, lineHeight: 1 }}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              <div style={{ padding: isMobile ? "8px 10px" : "12px 14px" }}>
                <ShippingForm isCollapsed={false} {...shippingFormProps} />
              </div>
            </div>
          )}

          {/* Form — full on step 1, compact summary on step 2+; sticky on left when results are visible */}
          {(!isMobile || currentStep === ShippingStep.FORM || !isMobileFormVisible) && (
            <div
              style={{
                width: "100%",
                position: !isMobile && currentStep !== ShippingStep.FORM ? "sticky" : "static",
                top: !isMobile && currentStep !== ShippingStep.FORM ? 88 : undefined,
                alignSelf: "start",
              }}
            >
              <ShippingForm
                isCollapsed={!isMobile && currentStep !== ShippingStep.FORM}
                {...shippingFormProps}
              />
            </div>
          )}

          {/* Results container — to the right of the form on desktop, hidden on FORM step */}
          <div
            ref={resultsRef}
            style={{
              width: "100%",
              display: currentStep === ShippingStep.FORM ? "none" : "block",
              position: "relative",
            }}
          >
            {/* Loading */}
            {currentStep === ShippingStep.LOADING && (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300, width: "100%" }}>
                <ShippingLoadingAnimation visible={true} />
              </div>
            )}

            {/* Rates */}
            {currentStep === ShippingStep.RATES && !isLoadingRates && (
              <div style={{ width: "100%" }}>
                <ShippingRates
                  visible={true}
                  rates={shippingRates}
                  selectedRateId={selectedRateId}
                  currency="ILS"
                  isRTL={isRTL}
                  totalWeight={calculateTotalWeight()}
                  unitSystem={unitSystem}
                  onRateSelect={handleRateSelect}
                  onContinue={handleContinueToDelivery}
                  onBack={() => updateCurrentStep(ShippingStep.FORM)}
                  showSortOptions={true}
                  scrollToTopWhenSelected={true}
                />
              </div>
            )}

            {/* Address selection */}
            {currentStep === ShippingStep.ADDRESS && (
              <div style={{ width: "100%" }}>
                {!isAuthenticated ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "48px 24px",
                      background: "var(--bg-elev)",
                      borderRadius: 12,
                      border: "1px solid var(--line)",
                    }}
                  >
                    <p style={{ fontSize: 18, fontWeight: 700, color: "var(--ink)", marginBottom: 8 }}>
                      🔐 Authentication Required
                    </p>
                    <p style={{ fontSize: 14, color: "var(--ink-4)", marginBottom: 24 }}>
                      Please sign in to proceed with address selection and complete your shipping order.
                    </p>
                    <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                      <Button variant="blue" onClick={() => setShowAuthDialog(true)} style={{ padding: "8px 24px" }}>
                        Sign In
                      </Button>
                      <Button variant="ghost" onClick={handleBackToRates} style={{ padding: "8px 24px" }}>
                        Back to Rates
                      </Button>
                    </div>
                  </div>
                ) : (
                  <AddressSelection
                    visible={true}
                    selectedSourceAddressId={selectedSourceAddress?.id || null}
                    selectedDestinationAddressId={selectedDestinationAddress?.id || null}
                    isRTL={isRTL}
                    onSourceAddressSelect={handleSourceAddressSelect}
                    onDestinationAddressSelect={handleDestinationAddressSelect}
                    onContinue={handleContinueToPayment}
                    onBack={handleBackToRates}
                    allowedOriginCountry={userOriginCountry}
                    allowedDestinationCountry={userDestinationCountry}
                    shippingData={{
                      shippingType,
                      shipmentType,
                      parcels,
                      documents,
                      unitSystem,
                      originPostalCode,
                      destinationPostalCode,
                    }}
                    onRateValidation={(success, rates) => {
                      if (success && rates) setShippingRates(rates);
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Auth dialog */}
        <AuthDialog open={showAuthDialog} onClose={handleAuthDialogClose} onSuccess={handleAuthSuccess} />

        {/* Mobile FAB: show form */}
        {isMobile && currentStep !== ShippingStep.FORM && !isMobileFormVisible && (
          <button
            type="button"
            onClick={toggleMobileForm}
            aria-label="modify search"
            style={{
              position: "fixed",
              bottom: 16,
              right: isRTL ? "auto" : 16,
              left: isRTL ? 16 : "auto",
              zIndex: 1000,
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "var(--brand-blue)",
              color: "white",
              border: "none",
              cursor: "pointer",
              fontSize: 20,
              boxShadow: "0 8px 24px rgba(59,130,246,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✏️
          </button>
        )}

        {/* Mobile FAB: scroll to top */}
        {isMobile && currentStep !== ShippingStep.FORM && !isMobileFormVisible && (
          <button
            type="button"
            aria-label="scroll to top"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            style={{
              position: "fixed",
              bottom: 80,
              right: isRTL ? "auto" : 16,
              left: isRTL ? 16 : "auto",
              zIndex: 1000,
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: "var(--bg-elev)",
              color: "var(--ink)",
              border: "1px solid var(--line)",
              cursor: "pointer",
              fontSize: 16,
              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ↑
          </button>
        )}
      </div>
    </div>
  );
};

export default ShippingCalculator;
